import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@cian/ui-kit';

import {
  addNewbuildingToComparison,
  IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../api/comparison';
import { requestAuthentication } from '../../actions/authentication';
import { setNewbuildingComparisonStatus } from '../../actions/offersData';
import { EComparisonStatus } from '../../repositories/newbuilding-search/entities/get_newbuildings_for_serp/NewbuildingForSerpItem';
import { getNewbuildingComparisonStatusSelector } from '../../selectors/newbuilding';
import { getIsAuthenticated } from '../../selectors/user';
import { TTypedThunkDispatch } from '../../types/thunk';
import { useApplicationContext } from '../../utils/applicationContext';
import { COMPARE_ENABLED_CONFIG } from './constants/compare';
import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareToastContent } from './CompareToastContent';
import { CompareButtonWithTooltip } from './CompareButtonWithTooltip';
import { useCompareOnboarding } from './hooks/useCompareOnboarding';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackRemoveNewbuildingFromComparison,
} from './tracking';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте ещё раз';

interface ICompareButtonProps {
  newbuildingId: number;
  gaLabel: string;
  withOnboarding?: boolean;
}

export const CompareButton: React.FC<ICompareButtonProps> = ({ newbuildingId, gaLabel, withOnboarding = false }) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch: TTypedThunkDispatch = useDispatch();
  const userIsAuthenticated = useSelector(getIsAuthenticated);
  const comparisonStatus = useSelector(getNewbuildingComparisonStatusSelector(newbuildingId));
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isAdded = comparisonStatus === EComparisonStatus.Added;

  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isCompareEnabled);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult, onClick?: () => void) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: <CompareToastContent text={result.text} comparisonUrl={result.comparisonUrl} onClick={onClick} />,
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextComparisonStatus = isAdded ? EComparisonStatus.Available : EComparisonStatus.Added;

    if (nextComparisonStatus === EComparisonStatus.Added && !userIsAuthenticated) {
      dispatch(requestAuthentication());

      return;
    }

    dispatch(setNewbuildingComparisonStatus(newbuildingId, nextComparisonStatus));

    try {
      const result =
        nextComparisonStatus === EComparisonStatus.Added
          ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
          : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      if (result.success === false) {
        showComparisonToast(result);
        dispatch(setNewbuildingComparisonStatus(newbuildingId, comparisonStatus || EComparisonStatus.Available));

        return;
      }

      if (nextComparisonStatus === EComparisonStatus.Added) {
        showComparisonToast(result, () => trackClickAddComparisonPopup(newbuildingId));
        trackAddNewbuildingToComparison(newbuildingId, gaLabel);
      } else {
        showComparisonToast(result, () => trackClickRemoveComparisonPopup(newbuildingId));
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel);
      }
    } catch (_err) {
      dispatch(setNewbuildingComparisonStatus(newbuildingId, comparisonStatus || EComparisonStatus.Available));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [
    comparisonStatus,
    dispatch,
    gaLabel,
    httpApi,
    isAdded,
    logger,
    newbuildingId,
    showComparisonToast,
    userIsAuthenticated,
  ]);

  if (!isCompareEnabled || !comparisonStatus || comparisonStatus === EComparisonStatus.Unavailable) {
    return null;
  }

  if (!withOnboarding || !open) {
    return <CompareButtonWithTooltip isAdded={isAdded} onClick={handleClick} />;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      <CompareButtonWithTooltip isAdded={isAdded} onClick={handleClick} />
    </CompareButtonOnboarding>
  );
};
